/**
 * interfaces
 */
import { IUploadProgressSegment, IUploadWatchModel } from './interfaces';
import { IUploadWatchEntry, IUploadWatchTarget } from '../../interfaces';

/**
 * models
 */
import { IUploadBatchModel } from '../../../models';

/**
 * enums
 */
import { UploadItemStatus } from '../../../enums';
import { IThemeColorVariant, ThemeColorsVariants } from '@core/enums/theme-colors-variants';

/**
 * One batch still being followed, ready to render.
 *
 * Built from the entry the watcher keeps and the last answer it had about
 * that batch, which is why the batch is allowed to be missing: a batch
 * queued a second ago is being watched before it has been asked after
 * once, and the menu has to show it from the moment the upload returns.
 *
 * The counts are read rather than kept, so a row rebuilt from a newer
 * answer cannot disagree with it.
 */
export class UploadWatchModel implements IUploadWatchModel {

    /**
     * @param batchId - Identifier of the batch.
     * @param typeLabel - What was uploaded, already worded for the screen:
     * the wording is a translation, and a model does not resolve those.
     * @param _entry - What the watcher is keeping about the batch.
     * @param _batch - The last answer about it, or null before the first.
     */
    constructor(
        public readonly batchId: string,
        public readonly typeLabel: string,
        private readonly _entry: IUploadWatchEntry,
        private readonly _batch: IUploadBatchModel | null,
    ) {}

    /** @inheritdoc */
    public get isPending(): boolean {
        return this._batch === null;
    }

    /** @inheritdoc */
    public get isFinished(): boolean {
        return this._batch?.processingCompleted === true;
    }

    /** @inheritdoc */
    public get succeeded(): number {
        return Math.max(this.processed - this.failed, 0);
    }

    /**
     * @inheritdoc
     *
     * The two shares are worked out in order rather than each on its own,
     * because rounding them separately can put their sum a point over the
     * track: what is left of a hundred after the read files is the most
     * the rejected ones may take.
     *
     * Both outcomes are always listed, the ones no file has reached yet
     * among them, and that is what makes the track move rather than jump.
     * A stretch is an element on screen, and an element only slides from
     * one width to another if it was already there to slide from: leaving
     * the empty outcomes out meant the green was *created* at the width of
     * the first files to settle — two of four arriving together put a bar
     * on screen at half the track with no width to have come from — and
     * the red was created at the first rejection, wherever in the batch
     * that fell. Only the moves after each first one had an element to
     * move, which is why the end of a batch was the part that looked
     * right.
     *
     * An outcome with no files to it is a stretch of no width, which shows
     * nothing, so a batch that has lost nothing still carries no red.
     */
    public get segments(): IUploadProgressSegment[] {

        const total = this.total;

        const succeeded = total === 0 ? 0 : Math.round((this.succeeded / total) * 100);

        const failed = total === 0 ? 0 : Math.min(Math.round((this.failed / total) * 100), 100 - succeeded);

        return [
            { variant: ThemeColorsVariants.Success, count: this.succeeded, value: succeeded },
            { variant: ThemeColorsVariants.Danger, count: this.failed, value: failed },
        ];
    }

    /** @inheritdoc */
    public get total(): number {

        if (this._batch === null) return 0;

        // the count the batch was created with, falling back to what has
        // been reported — a batch answers one or the other, never neither
        return this._batch.totalFiles || this._batch.items.length;
    }

    /**
     * @inheritdoc
     *
     * Counted off the per-file outcomes and never off how many rows came
     * back. The server reports every file of the batch from its first
     * answer, the ones it has not read yet among them, so counting the
     * rows was counting the batch twice over: it read as finished the
     * moment it was queued, and the bar it feeds went straight to full.
     *
     * A file is counted once it has settled — read or rejected, both are
     * outcomes and both are behind us. Only `Pending` is not.
     *
     * Held to the batch's own count, so a server reporting more outcomes
     * than the batch was created with cannot say five of four.
     */
    public get processed(): number {

        if (this._batch === null) return 0;

        const settled = this._batch.items.filter((item) => item.status !== UploadItemStatus.Pending).length;

        return Math.min(settled, this.total);
    }

    /** @inheritdoc */
    public get failed(): number {
        return this._batch?.items.filter((item) => item.status === UploadItemStatus.Failed).length ?? 0;
    }

    /**
     * @inheritdoc
     *
     * The share of the batch that has settled: four files are the whole
     * bar, and each one read or rejected is a quarter of it. There is no
     * measure of a single file's progress to be had — the upload was
     * answered the moment it was accepted, and what arrives afterwards is
     * outcomes, one file at a time — so the files themselves are the
     * only honest unit.
     *
     * A batch the server calls finished is full regardless, since the
     * watch ends in the same round the completion is seen and a bar left
     * short of the end would be the last thing the row showed.
     */
    public get progress(): number {

        if (this._batch?.processingCompleted === true) return 100;

        if (this.total === 0) return 0;

        return Math.min(Math.round((this.processed / this.total) * 100), 100);
    }

    /** @inheritdoc */
    public get startedAt(): number {
        return this._entry.startedAt;
    }

    /** @inheritdoc */
    public get target(): IUploadWatchTarget | undefined {
        return this._entry.target;
    }

}
